import { CanoeIcon, css, EllipsisIcon, Feedback, hex2rgba, Stack, styled, Tooltip } from 'canoe';
import React, { MouseEventHandler, useMemo, useRef } from 'react';

import { FeedbackColor } from 'canoe/components/Feedback';
import { TooltipPlacement } from 'canoe/components/Tooltip/Tooltip';

const TabButton = styled.button<{
  isActive: boolean;
  numOfErrors: number;
  severity: 'warning' | 'danger';
  hasThumbnail: boolean;
}>`
  ${({ theme, isActive, numOfErrors, severity, hasThumbnail }) => css`
    width: 60px;
    height: 100%;
    background-color: ${theme.colors.OnyxGrayL10};
    border-radius: ${theme.borderRadius.small};
    outline: none !important;
    border: 0 none;
    cursor: pointer;
    position: relative;
    padding: 0;

    ${
      !hasThumbnail &&
      css`
        display: flex;
        align-items: center;
        justify-content: center;
      `
    }

    ${
      isActive &&
      css`
        background-color: ${theme.colors.AquaTealL10};
        cursor: default;
      `
    }

    ${
      numOfErrors > 0 &&
      css`
        ::before {
          content: "${numOfErrors}";
          width: 13px;
          height: 13px;
          border-radius: 50%;
          font-size: 9px;
          line-height: 11px;
          color: ${theme.colors.White};
          position: absolute;
          left: -7px;
          top: -7px;
          background-color: ${
            severity === 'danger' ? theme.colors.ParadiseRedD20 : theme.colors.MangoOrange
          };
          box-sizing: content-box;
          border: 1px solid ${theme.colors.Contrast};
          z-index: 1;
        }
      `
    }
  `}
`;

const TabContextButton = styled.button<{ hasThumbnail: boolean }>`
  ${({ theme, hasThumbnail }) => css`
    width: 10px;
    height: 40px;
    padding: 0;
    background-color: ${hex2rgba(theme.colors.White, 0.2)};
    border-radius: 0 ${theme.borderRadius.small} ${theme.borderRadius.small} 0;
    display: flex;
    align-items: center;
    justify-content: center;
    outline: none !important;
    border: 0 none;
    position: absolute;
    top: 0;
    right: 0;
    cursor: pointer;

    ${hasThumbnail &&
    css`
      background-color: ${theme.colors.AquaTealL10};
    `}
  `}
`;

const StepThumbnailWrapper = styled.div<{ isActive: boolean }>`
  ${({ theme, isActive }) => css`
    width: 60px;
    height: 40px;
    flex-shrink: 0;
    position: relative;
    border-radius: ${theme.borderRadius.small};
    border: 1px solid ${theme.colors.Transparent};
    background-color: ${theme.colors.White};
    overflow: hidden;

    ${isActive &&
    css`
      border-color: ${theme.colors.AquaTealL10};
    `}
  `}
`;

const StepThumbnail = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  transform: scale(10.5%) translate(-428%, -428%);
  width: 570px;
  height: 380px;
`;

const TabContainer = styled.div<{ hasThumbnail: boolean; isActive: boolean }>`
  ${({ theme, hasThumbnail, isActive }) => css`
    height: 40px;
    position: relative;
    flex-shrink: 0;

    &:hover {
      ${TabButton} {
        background-color: ${isActive ? theme.colors.AquaTeal : theme.colors.OnyxGray};
      }
    }

    ${hasThumbnail &&
    css`
      &:hover {
        ${StepThumbnailWrapper} {
          border-color: ${isActive ? theme.colors.AquaTeal : theme.colors.OnyxGray};
        }

        ${TabContextButton} {
          ${isActive &&
          css`
            background-color: ${theme.colors.AquaTeal};
          `}
        }
      }
    `}
  `}
`;

const ErrorCalloutWrapper = styled.div`
  max-width: 240px;
`;

export enum TabErrorSeverity {
  Danger = 'danger',
  Warning = 'warning',
}

interface Props {
  id: string;
  nodeId: string;
  nodeName: string;
  isActive: boolean;
  tooltipPlacement: TooltipPlacement;
  onSelect: MouseEventHandler;
  onContextClick: (dom: HTMLButtonElement | null) => void;
  tabIcon?: CanoeIcon;
  tabThumbnail?: JSX.Element;
  errors?: { severity: TabErrorSeverity; content: string | React.ReactNode }[];
  isContextOpen: boolean;
}

export const FloatingMenuTab = React.memo(
  ({
    id,
    nodeId,
    nodeName,
    isActive,
    tooltipPlacement,
    onSelect,
    onContextClick,
    tabIcon: TabIcon,
    tabThumbnail,
    errors = [],
    isContextOpen,
  }: Props) => {
    const tabRef = useRef<HTMLButtonElement | null>(null);

    const showError = useMemo(
      () => errors.findIndex(({ severity }) => severity === TabErrorSeverity.Danger) > -1,
      [errors],
    );
    const showWarning = useMemo(
      () =>
        !showError &&
        errors.findIndex(({ severity }) => severity === TabErrorSeverity.Warning) > -1,
      [showError, errors],
    );

    const TabComponent = useMemo(() => {
      if (TabIcon && !tabThumbnail) {
        return <TabIcon color="White" />;
      }

      if (tabThumbnail) {
        return (
          <StepThumbnailWrapper isActive={isActive}>
            <StepThumbnail>{tabThumbnail}</StepThumbnail>
          </StepThumbnailWrapper>
        );
      }

      return <></>;
    }, [TabIcon, tabThumbnail, isActive]);

    return (
      <Tooltip
        content={
          <ErrorCalloutWrapper>
            <Stack space="xxs">
              {errors.map((error, idx) => (
                <Feedback
                  key={`${idx.toString()}-${error.severity}`}
                  color={error.severity as FeedbackColor}
                  mode="alert"
                >
                  {error.content}
                </Feedback>
              ))}
            </Stack>
          </ErrorCalloutWrapper>
        }
        placement={tooltipPlacement === 'top' ? 'bottom-start' : 'top-start'}
        state="invisible"
        offset={isContextOpen ? 'l' : 'xxs'}
        disabled={!showError && !showWarning}
      >
        <Tooltip
          content={`${nodeName}${isActive ? ' (Active)' : ''}`}
          placement={tooltipPlacement}
          offset="s"
        >
          <TabContainer id={id} hasThumbnail={!!tabThumbnail} isActive={isActive}>
            <TabButton
              ref={tabRef}
              isActive={isActive}
              type="button"
              onClick={!isActive ? onSelect : undefined}
              id={`floating-tab-${nodeId}`}
              numOfErrors={errors.length}
              severity={showError ? 'danger' : 'warning'}
              hasThumbnail={!!tabThumbnail}
            >
              {TabComponent}
            </TabButton>
            {isActive && (
              <TabContextButton
                onClick={(e) => {
                  e.stopPropagation();
                  onContextClick(tabRef.current);
                }}
                hasThumbnail={!!tabThumbnail}
              >
                <EllipsisIcon size="xs" color="White" />
              </TabContextButton>
            )}
          </TabContainer>
        </Tooltip>
      </Tooltip>
    );
  },
);
